'use client';

import { FullScreenLoader, Stack } from '@slims/ui/atoms';
import type { EmailOtpType } from '@supabase/supabase-js';
import {
	AuthField,
	AuthHeader,
	AuthNotice,
	AuthScreen,
	AuthSubmit,
	AuthTextButton,
} from '@slims/ui/organisms';
import { useRouter } from '@/lib/next/navigation';
import { useEffect, useState } from 'react';
import { createSupabaseBrowser } from '@/lib/supabase/client';
import { useUIStore } from '@/stores/ui-store';

/** Stato del link di recupero: in verifica, valido, oppure scaduto/già usato. */
type LinkStatus = 'checking' | 'valid' | 'invalid';

export default function UpdatePasswordPage() {
	const router = useRouter();
	// Lo stesso marchio del login: senza tema l'intestazione ripiegava sul
	// logo testuale, e l'invito sembrava portare su un altro sito.
	const theme = useUIStore((s) => s.theme);
	const [password, setPassword] = useState('');
	const [confirm, setConfirm] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [linkStatus, setLinkStatus] = useState<LinkStatus>('checking');

	// `updateUser` senza sessione fallisce con un messaggio Supabase in inglese
	// ("Auth session missing!") che non dice all'utente cosa fare: il link di
	// recupero è scaduto o è già stato usato. Lo verifichiamo prima di mostrare
	// il form. `getSession` attende l'inizializzazione del client, quindi il
	// token nell'URL è già stato scambiato; l'ascolto su `onAuthStateChange`
	// copre il caso in cui la sessione arrivi subito dopo.
	useEffect(() => {
		let active = true;
		const supabase = createSupabaseBrowser();

		// Link delle nostre email (INB-392): `?token_hash=…&type=…` sul nostro
		// dominio, da verificare qui. Gli altri (link di Supabase) portano già la
		// sessione nell'URL e bastano `getSession` e l'ascolto qui sotto.
		const params = new URLSearchParams(window.location.search);
		const tokenHash = params.get('token_hash');
		const type = params.get('type');
		if (tokenHash && type) {
			supabase.auth
				.verifyOtp({ token_hash: tokenHash, type: type as EmailOtpType })
				.then(({ data, error }) => {
					if (!active) return;
					// Il token è monouso: toglierlo dall'indirizzo evita che un
					// ricaricamento lo riprovi e mostri «link non valido».
					window.history.replaceState(null, '', window.location.pathname);
					setLinkStatus(!error && data.session ? 'valid' : 'invalid');
				});
		} else {
			supabase.auth.getSession().then(({ data: { session } }) => {
				if (active) setLinkStatus(session ? 'valid' : 'invalid');
			});
		}

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			if (active && session) setLinkStatus('valid');
		});

		return () => {
			active = false;
			subscription.unsubscribe();
		};
	}, []);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError('');

		if (password.length < 8) {
			setError('La password deve essere di almeno 8 caratteri.');
			return;
		}
		if (password !== confirm) {
			setError('Le password non coincidono.');
			return;
		}

		setLoading(true);
		const supabase = createSupabaseBrowser();
		const { error: updateError } = await supabase.auth.updateUser({ password });

		if (updateError) {
			setError(updateError.message);
			setLoading(false);
			return;
		}

		router.push('/');
	}

	if (linkStatus === 'checking') return <FullScreenLoader />;

	if (linkStatus === 'invalid') {
		return (
			<AuthScreen>
				<Stack gap="xl">
					<AuthHeader
					theme={theme}
						title="Link non più valido"
						subtitle="Richiedi una nuova email di reimpostazione"
					/>
					<AuthNotice>
						Il link per impostare la password è scaduto o è già stato usato. Torna
						all&apos;accesso e richiedine uno nuovo.
					</AuthNotice>
					<AuthTextButton onClick={() => router.push('/sign-in')}>
						Torna all&apos;accesso
					</AuthTextButton>
				</Stack>
			</AuthScreen>
		);
	}

	return (
		<AuthScreen>
			<Stack gap="xl">
				<AuthHeader
					theme={theme}
					title="Imposta la tua password"
					subtitle="Scegli una password per accedere in futuro"
				/>

				<form onSubmit={handleSubmit}>
					<Stack gap="lg">
						{error && <AuthNotice>{error}</AuthNotice>}

						<AuthField
							id="password"
							label="Nuova password"
							type="password"
							value={password}
							onChange={setPassword}
							required
							minLength={8}
							placeholder="Minimo 8 caratteri"
						/>

						<AuthField
							id="confirm"
							label="Conferma password"
							type="password"
							value={confirm}
							onChange={setConfirm}
							required
							placeholder="Ripeti la password"
						/>

						<AuthSubmit disabled={loading}>
							{loading ? 'Salvataggio...' : 'Imposta password e accedi'}
						</AuthSubmit>
					</Stack>
				</form>
			</Stack>
		</AuthScreen>
	);
}
